import { memo, useState } from 'react';
import { VStack } from '@library';
import useProductVariantsFilterOptions from '../../../hooks/useProductVariantsFilterOptions';
import type { BaseProductData } from '../../../types';
import filterProducts from '../../../util/filterProducts';
import ProductVariantsTable from '../ProductTables/ProductVariantsTable';
import SpinnerPage from '../../../../core/components/pages/SpinnerPage';
import useProductsWithRelatedData from '../../../queries/useProductsWithRelatedData';
import ProductBulkOrderFilterControls from './ProductBulkOrderFilterControls';

const initiateSiblingAndVariantFilters = (products: BaseProductData[]) => {
    const filters: {
        [name: string]: {
            type: string;
            value: string[];
        };
    } = {};

    Array.from(
        new Set(
            products
                .flatMap(({ siblingAttributes }) => siblingAttributes ?? [])
                .flatMap(({ name }) => name)
        )
    ).forEach(name => {
        filters[name] = { type: 'sibling', value: [] };
    });

    Array.from(
        new Set(
            products.flatMap(item =>
                item.variants.flatMap(
                    variant =>
                        variant.variationAttributes?.map(({ name }) => name) ??
                        []
                )
            )
        )
    ).forEach(name => {
        filters[name] = { type: 'variant', value: [] };
    });

    return filters;
};

const ProductBulkOrder = ({ products }: { products: BaseProductData[] }) => {
    const [siblingAndVariantFilters, setSiblingAndVariantFilters] = useState<{
        [name: string]: {
            type: string;
            value: string[];
        };
    }>(initiateSiblingAndVariantFilters(products));

    const filters = {
        ...siblingAndVariantFilters,
    };

    const { productsWithRelatedData, loading } =
        useProductsWithRelatedData(products);

    const [areFiltersActive, filteredProducts] = productsWithRelatedData
        ? filterProducts(productsWithRelatedData, filters)
        : [false, []];

    const {
        options: filterOptions,
        showSiblingFilter,
        showVariantsFilter,
        shouldShowSiblingsColumn,
        shouldShowVariantsColumn,
        totalFiltersCount,
    } = useProductVariantsFilterOptions(products, filters);

    return (
        <div data-test-class="productVariantsTableCollapsibleTrigger">
            <VStack gap="medium">
                {loading ? (
                    <SpinnerPage />
                ) : (
                    <>
                        <ProductBulkOrderFilterControls
                            areFiltersActive={areFiltersActive}
                            totalFiltersCount={totalFiltersCount}
                            filters={filters}
                            filterOptions={filterOptions}
                            showSiblingFilter={showSiblingFilter}
                            showVariantsFilter={showVariantsFilter}
                            setSiblingAndVariantFilters={
                                setSiblingAndVariantFilters
                            }
                        />
                        <ProductVariantsTable
                            shouldShowSiblingsColumn={shouldShowSiblingsColumn}
                            shouldShowVariantsColumn={shouldShowVariantsColumn}
                            products={filteredProducts}
                            showDiscountColumn
                        />
                    </>
                )}
            </VStack>
        </div>
    );
};

export default memo(ProductBulkOrder);
